import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Search, MapPin, Phone, Globe, Star, Edit2, Trash2, ExternalLink, ThumbsUp, Mail, User, Award, Crown, Sparkles } from 'lucide-react';

const SERVICE_TYPES = [
  { value: '', label: 'All Services' },
  { value: 'vet', label: 'Veterinarian' },
  { value: 'farrier', label: 'Farrier' },
  { value: 'chiropractic', label: 'Chiropractic' },
  { value: 'massage', label: 'Equine Massage' },
  { value: 'acupuncture', label: 'Acupuncture' },
  { value: 'dentist', label: 'Equine Dentist' },
  { value: 'feed_store', label: 'Feed Store' },
  { value: 'tack_shop', label: 'Tack Shop' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'nutritionist', label: 'Nutritionist' },
  { value: 'other', label: 'Other' },
];

const TYPE_COLORS = {
  vet: { bg: 'bg-[#E9EFE8]', text: 'text-[#2D6A4F]' },
  farrier: { bg: 'bg-[#FEF3C7]', text: 'text-[#92400E]' },
  chiropractic: { bg: 'bg-[#F3E8FF]', text: 'text-[#7C3AED]' },
  massage: { bg: 'bg-[#FCE7F3]', text: 'text-[#BE185D]' },
  acupuncture: { bg: 'bg-[#DBEAFE]', text: 'text-[#1D4ED8]' },
  dentist: { bg: 'bg-[#E0E7FF]', text: 'text-[#3730A3]' },
  feed_store: { bg: 'bg-[#D1FAE5]', text: 'text-[#065F46]' },
  tack_shop: { bg: 'bg-[#FEF9C3]', text: 'text-[#854D0E]' },
  trainer: { bg: 'bg-[#FFE4E6]', text: 'text-[#9F1239]' },
  nutritionist: { bg: 'bg-[#CCFBF1]', text: 'text-[#0F766E]' },
  other: { bg: 'bg-[#F3F4F6]', text: 'text-[#4B5563]' },
};

const getBadge = (count) => {
  if (count >= 20) return { label: 'Top Rated', icon: Crown, color: 'text-[#D97706] bg-[#FEF3C7]' };
  if (count >= 10) return { label: 'Community Favorite', icon: Award, color: 'text-[#7C3AED] bg-[#F3E8FF]' };
  if (count >= 5) return { label: 'Rising Star', icon: Sparkles, color: 'text-[#2D6A4F] bg-[#E9EFE8]' };
  return null;
};

const emptyForm = {
  business_name: '', contact_name: '', service_type: 'vet', description: '', phone: '', email: '',
  website: '', zip_code: '', city: '', state: '', recommendation: ''
};

export default function ProviderDirectoryPage() {
  const { authHeaders, API, user, hasFullAccess } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [zipFilter, setZipFilter] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const fetchProviders = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterType) params.set('service_type', filterType);
      if (zipFilter) params.set('zip_code', zipFilter);
      const res = await axios.get(`${API}/providers?${params}`, authHeaders());
      setProviders(res.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProviders(); }, []); // eslint-disable-line
  useEffect(() => { fetchProviders(); }, [search, filterType, zipFilter]); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editProvider) {
        await axios.put(`${API}/providers/${editProvider.id}`, form, authHeaders());
        toast.success('Provider updated!');
      } else {
        await axios.post(`${API}/providers`, form, authHeaders());
        toast.success('Provider recommended!');
      }
      setShowDialog(false); setEditProvider(null); setForm({ ...emptyForm });
      fetchProviders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this recommendation?')) return;
    try {
      await axios.delete(`${API}/providers/${id}`, authHeaders());
      toast.success('Removed');
      fetchProviders();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const handleEndorse = async (id) => {
    try {
      await axios.post(`${API}/providers/${id}/endorse`, {}, authHeaders());
      fetchProviders();
    } catch (err) { toast.error('Failed to endorse'); }
  };

  const openAdd = () => {
    if (!hasFullAccess) { toast.error('Subscribe to recommend providers.'); return; }
    setEditProvider(null); setForm({ ...emptyForm }); setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditProvider(p);
    setForm({
      business_name: p.business_name || '', contact_name: p.contact_name || '',
      service_type: p.service_type || 'vet',
      description: p.description || '', phone: p.phone || '', email: p.email || '',
      website: p.website || '', zip_code: p.zip_code || '', city: p.city || '',
      state: p.state || '', recommendation: p.recommendation || ''
    });
    setShowDialog(true);
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 animate-fade-in">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="providers-title">
              Service Provider Directory
            </h1>
            <p className="text-[#4B6B58] mt-1">Community-recommended equine service providers. Share the great ones!</p>
          </div>
          <button onClick={openAdd} data-testid="add-provider-button" className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]">
            <ThumbsUp size={18} /> Recommend
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6 animate-fade-in-delay-1">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search providers..." data-testid="provider-search" className="stable-input w-full pl-9 py-2 text-sm" />
          </div>
          <div className="relative min-w-[100px]">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
            <input value={zipFilter} onChange={e => setZipFilter(e.target.value)} placeholder="Zip code" data-testid="provider-zip" className="stable-input w-full pl-8 py-2 text-sm" maxLength={5} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {SERVICE_TYPES.slice(0, 7).map(t => (
              <button key={t.value} onClick={() => setFilterType(t.value === filterType ? '' : t.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === t.value ? 'bg-[#1B4332] text-white' : 'bg-white text-[#4B6B58] hover:bg-[#E9EFE8]'}`}>
                {t.label}
              </button>
            ))}
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white text-[#4B6B58] border border-[#D1E0D5]">
              {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Provider Cards */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[1,2,3].map(i => <div key={i} className="bg-white rounded-[2rem] h-48 animate-pulse" />)}</div>
        ) : providers.length === 0 ? (
          <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="no-providers">
            <Star size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">No Providers Yet</h3>
            <p className="text-sm text-[#4B6B58]">Be the first to recommend a great equine service provider!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map(p => {
              const colors = TYPE_COLORS[p.service_type] || TYPE_COLORS.other;
              const typeLabel = SERVICE_TYPES.find(t => t.value === p.service_type)?.label || p.service_type;
              const endorsements = p.endorsements || [];
              const endorseCount = endorsements.length;
              const hasEndorsed = endorsements.includes(user?.id);
              const badge = getBadge(endorseCount);
              return (
                <div key={p.id} className="bg-white rounded-[2rem] overflow-hidden card-hover shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid={`provider-${p.id}`}>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${colors.bg} ${colors.text}`}>
                          {typeLabel}
                        </span>
                        {badge && (
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 ${badge.color}`} data-testid={`badge-${p.id}`}>
                            <badge.icon size={11} /> {badge.label}
                          </span>
                        )}
                      </div>
                      {p.user_id === user?.id && (
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(p)} className="p-1.5 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Edit2 size={14} /></button>
                          <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                    <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] mb-0.5">{p.business_name}</h3>
                    {p.contact_name && (
                      <p className="text-xs text-[#4B6B58] mb-1 flex items-center gap-1"><User size={11} /> {p.contact_name}</p>
                    )}
                    {p.description && <p className="text-sm text-[#4B6B58] mb-3 line-clamp-2">{p.description}</p>}

                    {p.recommendation && (
                      <div className="bg-[#F9F7F3] rounded-xl p-3 mb-3">
                        <p className="text-xs text-[#4B6B58] italic">"{p.recommendation}"</p>
                        <p className="text-[10px] text-[#4B6B58] mt-1 font-medium">- {p.added_by}</p>
                      </div>
                    )}

                    <div className="space-y-1.5 text-xs text-[#4B6B58]">
                      {(p.city || p.state || p.zip_code) && (
                        <p className="flex items-center gap-1.5"><MapPin size={12} /> {[p.city, p.state, p.zip_code].filter(Boolean).join(', ')}</p>
                      )}
                      {p.phone && <p className="flex items-center gap-1.5"><Phone size={12} /> {p.phone}</p>}
                      {p.email && <p className="flex items-center gap-1.5"><Mail size={12} /> {p.email}</p>}
                      {p.website && (
                        <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#2D6A4F] hover:underline">
                          <Globe size={12} /> Website <ExternalLink size={10} />
                        </a>
                      )}
                    </div>

                    {/* Endorse Button */}
                    <div className="mt-4 pt-3 border-t border-[#E9EFE8] flex items-center justify-between">
                      <button
                        onClick={() => handleEndorse(p.id)}
                        data-testid={`endorse-${p.id}`}
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                          hasEndorsed
                            ? 'bg-[#2D6A4F] text-white shadow-sm'
                            : 'bg-[#E9EFE8] text-[#2D6A4F] hover:bg-[#D1E0D5]'
                        }`}
                      >
                        <ThumbsUp size={13} /> {hasEndorsed ? 'Endorsed' : 'Endorse'}
                      </button>
                      <span className="text-xs text-[#4B6B58] font-medium">{endorseCount} endorsement{endorseCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="provider-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">{editProvider ? 'Edit Recommendation' : 'Recommend a Provider'}</DialogTitle>
            <DialogDescription className="text-[#4B6B58]">Share a great equine service provider with the community.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Business Name *</label>
                <input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} required data-testid="provider-name-input" className="stable-input w-full" placeholder="e.g., Smith Equine Clinic" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Contact Person</label>
                <input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} data-testid="provider-contact-name-input" className="stable-input w-full" placeholder="e.g., Dr. Jane Smith" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Service Type *</label>
                <select value={form.service_type} onChange={e => setForm({...form, service_type: e.target.value})} data-testid="provider-type-select" className="stable-input w-full">
                  {SERVICE_TYPES.filter(t => t.value).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Email</label>
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="provider-email-input" className="stable-input w-full" placeholder="contact@example.com" type="email" />
              </div>
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">What makes them great?</label>
              <textarea value={form.recommendation} onChange={e => setForm({...form, recommendation: e.target.value})} data-testid="provider-rec-input" className="stable-input w-full" rows={3} placeholder="They're amazing because..." />
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Description</label>
              <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="provider-desc-input" className="stable-input w-full" placeholder="Brief description of services offered" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">City</label>
                <input value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="provider-city-input" className="stable-input w-full" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">State</label>
                <input value={form.state} onChange={e => setForm({...form, state: e.target.value})} data-testid="provider-state-input" className="stable-input w-full" maxLength={2} placeholder="OR" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Zip Code</label>
                <input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} data-testid="provider-zip-input" className="stable-input w-full" maxLength={5} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Phone</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="provider-phone-input" className="stable-input w-full" placeholder="555-123-4567" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Website</label>
                <input value={form.website} onChange={e => setForm({...form, website: e.target.value})} data-testid="provider-website-input" className="stable-input w-full" placeholder="www.example.com" />
              </div>
            </div>
            <button type="submit" data-testid="provider-save-button" className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">{editProvider ? 'Update' : 'Recommend Provider'}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
